import { useId } from "react";
import type { DataRow } from "./reportApi";
import { SERIES_COLORS } from "./types";
import { useTranslation } from "../../i18n";

// Lightweight, dependency-free SVG charts. They scale to their container width
// via a viewBox and preserveAspectRatio, so they print crisply (vector) too.

interface ChartProps {
  rows: DataRow[];
  color: string;
  format: (n: number) => string;
  showValues?: boolean;
}

function Empty() {
  const { t } = useTranslation();
  return (
    <div className="flex h-full min-h-[8rem] items-center justify-center text-sm text-gray-300">
      {t("reportBuilder.noDataSelection", "No data for this selection")}
    </div>
  );
}

function niceMax(max: number): number {
  if (max <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(max)));
  const n = max / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

// ---------------------------------------------------------------------------
// Bar chart (vertical or horizontal).
// ---------------------------------------------------------------------------
export function BarChartViz({
  rows,
  color,
  format,
  showValues = true,
  orientation = "vertical",
}: ChartProps & { orientation?: "horizontal" | "vertical" }) {
  if (!rows.length) return <Empty />;
  const max = niceMax(Math.max(...rows.map((r) => r.value), 0));

  if (orientation === "horizontal") {
    return (
      <div className="space-y-2">
        {rows.map((r, i) => {
          const pct = max > 0 ? (r.value / max) * 100 : 0;
          return (
            <div key={i}>
              <div className="mb-0.5 flex justify-between text-xs">
                <span className="truncate pr-2 text-gray-700">{r.label}</span>
                {showValues && <span className="font-semibold text-gray-900">{format(r.value)}</span>}
              </div>
              <div className="h-3 w-full rounded-sm bg-gray-100">
                <div
                  className="h-3 rounded-sm transition-all"
                  style={{ width: `${pct}%`, backgroundColor: color }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Vertical bars rendered as SVG.
  const W = 600;
  const H = 260;
  const padL = 44;
  const padB = 46;
  const padT = 12;
  const innerW = W - padL - 12;
  const innerH = H - padB - padT;
  const n = rows.length;
  const gap = Math.max(6, innerW / n / 4);
  const barW = (innerW - gap * (n - 1)) / n;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      {/* gridlines */}
      {[0, 0.25, 0.5, 0.75, 1].map((t) => {
        const y = padT + innerH * (1 - t);
        return (
          <g key={t}>
            <line x1={padL} y1={y} x2={W - 12} y2={y} stroke="#e5e7eb" strokeWidth={1} />
            <text x={padL - 6} y={y + 3} textAnchor="end" fontSize={9} fill="#94a3b8">
              {format(max * t)}
            </text>
          </g>
        );
      })}
      {rows.map((r, i) => {
        const h = max > 0 ? (r.value / max) * innerH : 0;
        const x = padL + i * (barW + gap);
        const y = padT + innerH - h;
        const label = r.label.length > 10 ? r.label.slice(0, 9) + "…" : r.label;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={h} rx={3} fill={color} />
            {showValues && h > 14 && (
              <text x={x + barW / 2} y={y - 3} textAnchor="middle" fontSize={9} fill="#334155" fontWeight={600}>
                {format(r.value)}
              </text>
            )}
            <text
              x={x + barW / 2}
              y={H - padB + 14}
              textAnchor="middle"
              fontSize={9}
              fill="#64748b"
              transform={n > 8 ? `rotate(35 ${x + barW / 2} ${H - padB + 14})` : undefined}
            >
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Line chart.
// ---------------------------------------------------------------------------
export function LineChartViz({ rows, color, format }: ChartProps) {
  const gradId = useId().replace(/:/g, "");
  if (!rows.length) return <Empty />;
  const W = 620;
  const H = 260;
  const padL = 46;
  const padB = 40;
  const padT = 14;
  const innerW = W - padL - 14;
  const innerH = H - padB - padT;
  const max = niceMax(Math.max(...rows.map((r) => r.value), 0));
  const n = rows.length;
  const x = (i: number) => padL + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const y = (v: number) => padT + innerH - (max > 0 ? (v / max) * innerH : 0);

  const linePts = rows.map((r, i) => `${x(i)},${y(r.value)}`).join(" ");
  const areaPts = `${x(0)},${padT + innerH} ${linePts} ${x(n - 1)},${padT + innerH}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id={`g${gradId}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.28} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      {[0, 0.25, 0.5, 0.75, 1].map((t) => {
        const gy = padT + innerH * (1 - t);
        return (
          <g key={t}>
            <line x1={padL} y1={gy} x2={W - 14} y2={gy} stroke="#e5e7eb" strokeWidth={1} />
            <text x={padL - 6} y={gy + 3} textAnchor="end" fontSize={9} fill="#94a3b8">
              {format(max * t)}
            </text>
          </g>
        );
      })}
      <polygon points={areaPts} fill={`url(#g${gradId})`} />
      <polyline points={linePts} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
      {rows.map((r, i) => (
        <circle key={i} cx={x(i)} cy={y(r.value)} r={3} fill="#fff" stroke={color} strokeWidth={2} />
      ))}
      {rows.map((r, i) => {
        if (n > 12 && i % Math.ceil(n / 12) !== 0) return null;
        const label = r.label.length > 8 ? r.label.slice(0, 7) + "…" : r.label;
        return (
          <text key={i} x={x(i)} y={H - padB + 16} textAnchor="middle" fontSize={9} fill="#64748b">
            {label}
          </text>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Pie / Donut chart.
// ---------------------------------------------------------------------------
export function PieChartViz({
  rows,
  format,
  donut = true,
}: ChartProps & { donut?: boolean }) {
  if (!rows.length) return <Empty />;
  const total = rows.reduce((s, r) => s + r.value, 0);
  const R = 80;
  const cx = 90;
  const cy = 90;
  const inner = donut ? 46 : 0;

  let angle = -Math.PI / 2;
  const slices = rows.map((r, i) => {
    const frac = total > 0 ? r.value / total : 0;
    const start = angle;
    const end = angle + frac * Math.PI * 2;
    angle = end;
    const large = end - start > Math.PI ? 1 : 0;
    const x1 = cx + R * Math.cos(start);
    const y1 = cy + R * Math.sin(start);
    const x2 = cx + R * Math.cos(end);
    const y2 = cy + R * Math.sin(end);
    let d: string;
    if (inner > 0) {
      const xi1 = cx + inner * Math.cos(end);
      const yi1 = cy + inner * Math.sin(end);
      const xi2 = cx + inner * Math.cos(start);
      const yi2 = cy + inner * Math.sin(start);
      d = `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${xi1} ${yi1} A ${inner} ${inner} 0 ${large} 0 ${xi2} ${yi2} Z`;
    } else {
      d = `M ${cx} ${cy} L ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} Z`;
    }
    return { d, color: SERIES_COLORS[i % SERIES_COLORS.length], row: r, frac };
  });

  return (
    <div className="flex flex-wrap items-center gap-4">
      <svg viewBox="0 0 180 180" className="h-44 w-44 shrink-0">
        {total === 0 ? (
          <circle cx={cx} cy={cy} r={R} fill="#eef2f8" />
        ) : (
          slices.map((s, i) => <path key={i} d={s.d} fill={s.color} stroke="#fff" strokeWidth={1.5} />)
        )}
        {donut && (
          <text x={cx} y={cy + 4} textAnchor="middle" fontSize={16} fontWeight={700} fill="#1e293b">
            {format(total)}
          </text>
        )}
      </svg>
      <div className="min-w-[8rem] flex-1 space-y-1">
        {slices.map((s, i) => (
          <div key={i} className="flex items-center justify-between gap-2 text-xs">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: s.color }} />
              <span className="truncate text-gray-700">{s.row.label}</span>
            </span>
            <span className="shrink-0 font-semibold text-gray-900">
              {format(s.row.value)}
              <span className="ml-1 font-normal text-gray-400">{Math.round(s.frac * 100)}%</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comparison chart — two series side by side, matched on label.
// ---------------------------------------------------------------------------
export function ComparisonViz({
  rowsA,
  rowsB,
  labelA,
  labelB,
  format,
}: {
  rowsA: DataRow[];
  rowsB: DataRow[];
  labelA: string;
  labelB: string;
  format: (n: number) => string;
}) {
  if (!rowsA.length && !rowsB.length) return <Empty />;
  const mapB = new Map(rowsB.map((r) => [r.label, r.value]));
  const labels = rowsA.map((r) => r.label);
  const max = niceMax(Math.max(...rowsA.map((r) => r.value), ...rowsB.map((r) => r.value), 0));

  return (
    <div>
      <div className="mb-2 flex gap-4 text-xs">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: SERIES_COLORS[0] }} /> {labelA}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: SERIES_COLORS[1] }} /> {labelB}
        </span>
      </div>
      <div className="space-y-3">
        {labels.map((lbl, i) => {
          const a = rowsA[i]?.value || 0;
          const b = mapB.get(lbl) || 0;
          return (
            <div key={i}>
              <div className="mb-0.5 text-xs text-gray-700">{lbl}</div>
              <div className="flex items-center gap-1.5">
                <div className="h-2.5 rounded-sm" style={{ width: `${(a / max) * 100}%`, backgroundColor: SERIES_COLORS[0], minWidth: 2 }} />
                <span className="text-[0.65rem] text-gray-500">{format(a)}</span>
              </div>
              <div className="mt-1 flex items-center gap-1.5">
                <div className="h-2.5 rounded-sm" style={{ width: `${(b / max) * 100}%`, backgroundColor: SERIES_COLORS[1], minWidth: 2 }} />
                <span className="text-[0.65rem] text-gray-500">{format(b)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
